import { Dialog } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/Button';
import { SHIPPING_FEE } from '../../../consts';
import { useSession } from '../../../context/SessionContext';
import { useSystemContext } from '../../../context/SystemContext/SystemContext';
import { useUserMenuContext } from '../../../context/UserMenuContext';
import useCreateOrder from '../../../hook/useCreateOrder';
import PAYMENT from '../../DeliveryInformation/components/Payment';
import PaymentNoticeDialog from '../../../components/PaymentNoticeDialog/PaymentNoticeDialog';
import { getPaymentNoticeAlerts } from '../../../utils/cancellationAlert';
import { calculateOrderItemsTotal } from '../../../utils';

interface PaymentBacklogProps {
  orderDetail: any;
  multiOrder?: boolean;
}

const PaymentBacklog: React.FC<PaymentBacklogProps> = ({ orderDetail, multiOrder = false }) => {
  const { t } = useTranslation();
  const [openPayment, setOpenPayment] = useState(false);
  const [openPaymentNotice, setOpenPaymentNotice] = useState(false);
  const [isSubmittingPaymentNotice, setIsSubmittingPaymentNotice] = useState(false);
  const { checkOrderV2, checkOrder } = useCreateOrder();
  const [openError, setOpenError] = useState(false);
  const [errorFood, setErrorFood] = useState<string>('');
  const { checkSessionExpiry } = useSession();
  const { stadiumInfo } = useSystemContext();
  const { listRestaurantData, listRestaurantWithFood } = useUserMenuContext();
  const isPreOrderPayment = orderDetail?.is_pre_order === true || String(orderDetail?.order_type || '').includes('pre_order');

  const [totalPrice] = useMemo(() => {
    if (!orderDetail) return [0];

    const totalPrice = calculateOrderItemsTotal(orderDetail);
    const formattedPrice = totalPrice + SHIPPING_FEE;
    return [formattedPrice];
  }, [orderDetail]);
  const paymentNoticeAlerts = useMemo(
    () =>
      getPaymentNoticeAlerts({
        order: orderDetail ?? {},
        restaurants: [...listRestaurantWithFood, ...listRestaurantData],
        language: stadiumInfo?.language,
      }),
    [listRestaurantData, listRestaurantWithFood, orderDetail, stadiumInfo?.language]
  );

  const handlePlaceOrder = async () => {
    if (!multiOrder && checkSessionExpiry()) return;
    const isErrorOrder = multiOrder ? await checkOrderV2(orderDetail) : await checkOrder(orderDetail);
    if (isErrorOrder) {
      setOpenPayment(true);
    } else {
      const errorFoodItem = sessionStorage.getItem('ErrorFood');
      if (errorFoodItem) {
        setErrorFood(errorFoodItem);
      }
      setOpenError(true);
    }
  };

  const handleClosePayment = () => {
    setOpenPayment(false);
  };

  const handleCompletePayment = async () => {
    if (isSubmittingPaymentNotice) return;
    if (multiOrder && isPreOrderPayment && paymentNoticeAlerts.length > 0) {
      setOpenPaymentNotice(true);
      return;
    }

    setIsSubmittingPaymentNotice(true);
    try {
      await handlePlaceOrder();
    } finally {
      setIsSubmittingPaymentNotice(false);
    }
  };

  const handleSubmitPaymentNotice = async () => {
    if (isSubmittingPaymentNotice) return;

    setIsSubmittingPaymentNotice(true);
    try {
      setOpenPaymentNotice(false);
      await handlePlaceOrder();
    } finally {
      setIsSubmittingPaymentNotice(false);
    }
  };

  const handleCancelPaymentNotice = () => {
    if (isSubmittingPaymentNotice) return;
    setOpenPaymentNotice(false);
  };

  const handleCloseErrorModal = () => {
    setOpenError(false);
    sessionStorage.removeItem('ErrorFood');
  };

  return (
    <div className="flex flex-col gap-2 items-center">
      <p className="text-black-500 font-normal text-sm leading-5">{t('CONTINUE_PAY')}</p>
      <Button onClick={handleCompletePayment} type="submit" disabled={isSubmittingPaymentNotice}>
        {t('COMPLETE_PAYMENT')}
      </Button>
      <PaymentNoticeDialog
        open={openPaymentNotice}
        onCancel={handleCancelPaymentNotice}
        onSubmit={handleSubmitPaymentNotice}
        isSubmitting={isSubmittingPaymentNotice}
        alerts={paymentNoticeAlerts}
      />
      <PAYMENT open={openPayment} onClose={handleClosePayment} totalPrice={totalPrice} orderId={orderDetail.id} restaurant_id={orderDetail.restaurant_id} />
      <Dialog open={openError} onClose={handleCloseErrorModal} sx={{ '& .MuiPaper-root': { borderRadius: '12px' } }}>
        <div className="p-6 flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <p className="text-black-900 text-lg font-semibold leading-7">{errorFood ? `${errorFood} ${t('HAS_BEEN_DISABLE')}` : t('LISTRESTAURANT_MODAL_TITLE')}</p>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PaymentBacklog;
